import type { ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Field, FieldLabel } from '@quebon/ui/components/field'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@quebon/ui/components/select'
import { Spinner } from '@quebon/ui/components/spinner'
import clsx from 'clsx'
import {
  FUNNEL_SYNTHETIC_STAGE_LABEL,
  LEAD_PRIORITY_LABEL,
  LEAD_STAGE_LABEL,
  STALE_LEVEL_TEXT,
  daysSinceKst,
  labelOf,
  staleLevel,
} from './crmLabels'

/**
 * 라벨을 보여 주는 select.
 *
 * base-ui `SelectValue`는 기본적으로 **값 자체**를 렌더한다. 포맷터를 주지 않으면 트리거에
 * `NEW_LEAD`나 `all` 같은 원문이 그대로 보인다. 화면마다 이 실수를 반복하지 않도록 한 곳에 둔다.
 */
export function LabeledSelect({
  id,
  value,
  items,
  onChange,
  ariaLabel,
  className,
  size,
}: {
  id?: string
  value: string
  items: Array<{ value: string; label: string }>
  onChange: (value: string) => void
  ariaLabel: string
  className?: string
  size?: 'sm' | 'default'
}) {
  const labelFor = (raw: unknown) => {
    const key = raw === null || raw === undefined ? '' : String(raw)
    return items.find((item) => item.value === key)?.label ?? key
  }

  return (
    <Select
      value={value}
      onValueChange={(next) => {
        if (next === null || next === undefined) return
        onChange(String(next))
      }}
    >
      <SelectTrigger id={id} size={size} className={clsx('w-full', className)} aria-label={ariaLabel}>
        <SelectValue>{labelFor}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

/** filter bar용 select. 전체를 뜻하는 값은 빈 문자열이 아니라 `all`이다 — base-ui Select는 빈 값을 placeholder로 본다. */
export function FilterSelect({
  label,
  value,
  items,
  onChange,
  allLabel = '전체',
  className,
}: {
  label: string
  value: string | undefined
  items: Array<{ value: string; label: string }>
  onChange: (value: string | undefined) => void
  allLabel?: string
  className?: string
}) {
  return (
    <Field className={className}>
      <FieldLabel>{label}</FieldLabel>
      <LabeledSelect
        ariaLabel={label}
        value={value ?? 'all'}
        items={[{ value: 'all', label: allLabel }, ...items]}
        onChange={(next) => onChange(next === 'all' ? undefined : next)}
      />
    </Field>
  )
}

export function Pagination({
  page,
  totalPages,
  totalElements,
  onChange,
}: {
  page: number
  totalPages: number
  totalElements: number
  onChange: (page: number) => void
}) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">총 {totalElements.toLocaleString('ko-KR')}건</p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="이전 페이지"
          disabled={page <= 0}
          onClick={() => onChange(page - 1)}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="px-2 text-sm tabular-nums">
          {page + 1} / {Math.max(totalPages, 1)}
        </span>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="다음 페이지"
          disabled={page >= totalPages - 1}
          onClick={() => onChange(page + 1)}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  )
}

export function StageBadge({ stage }: { stage: string }) {
  // 퍼널의 합성 단계(리드 이전, 접수함 데이터)는 리드 단계와 구분되는 outline으로 보여준다.
  if (stage in FUNNEL_SYNTHETIC_STAGE_LABEL) {
    return <Badge variant="outline">{labelOf(FUNNEL_SYNTHETIC_STAGE_LABEL, stage)}</Badge>
  }
  return (
    <Badge variant={stage === 'HOLD_CLOSED' ? 'destructive' : stage === 'CONTRACT' ? 'default' : 'secondary'}>
      {labelOf(LEAD_STAGE_LABEL, stage)}
    </Badge>
  )
}

export function PriorityBadge({ priority }: { priority: string }) {
  const emphasized = priority === 'CONTRACT_IMMINENT' || priority === 'CONTRACT_LIKELY'
  return (
    <Badge variant={emphasized ? 'default' : 'outline'}>
      {labelOf(LEAD_PRIORITY_LABEL, priority)}
    </Badge>
  )
}

/**
 * 체류일 (설계 §11.1). 31~60일 경고, 61일 이상 위험.
 *
 * 색만으로 전달하지 않는다 — 위험 구간에는 텍스트 라벨을 함께 붙인다.
 */
export function StaleDaysCell({
  lastActivityAt,
  stageEnteredAt,
}: {
  lastActivityAt: string
  stageEnteredAt?: string
}) {
  const days = daysSinceKst(lastActivityAt)
  const level = staleLevel(days)
  const stageDays = stageEnteredAt ? daysSinceKst(stageEnteredAt) : null

  return (
    <div className="flex flex-col gap-0.5">
      <span
        className={clsx(
          'tabular-nums',
          level === 'danger' && 'font-semibold text-destructive',
          level === 'warning' && 'font-medium text-primary',
        )}
      >
        {days === null ? '-' : `${days}일`}
        {level !== 'normal' && (
          <span className="ml-1 text-xs font-normal">({STALE_LEVEL_TEXT[level]})</span>
        )}
      </span>
      {stageDays !== null && (
        <span className="text-xs text-muted-foreground tabular-nums">단계 {stageDays}일</span>
      )}
    </div>
  )
}

export function LoadingBlock({ label = '불러오는 중' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
      <Spinner className="size-5" />
      {label}
    </div>
  )
}

export function ErrorBlock({ message }: { message?: string }) {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
      {message ?? '데이터를 불러오지 못했습니다. 새로고침을 시도해 주세요.'}
    </div>
  )
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="py-12 text-center text-sm text-muted-foreground">
        {children}
      </td>
    </tr>
  )
}

export function DefinitionRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border py-2 last:border-b-0">
      <dt className="shrink-0 text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm text-foreground">{children}</dd>
    </div>
  )
}
